import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';

import AddToTargetListButton from './AddToTargetListButton';
import { getApiAxiosInstance } from '../../../../utils/network';
import styles from '../collegeCoachActivity.scss';

export const INTERESTED_IN_ME_ENDPOINT = '/v1/cpp/coaches-interested-in-me';
export const INTERESTED_IN_ME_INLINE_PAGE_SIZE = 5;
const TESTID_PREFIX = 'profile.college-coach-activity.interested-in-me';

const formatDate = (date) => {
    if (!date) {
        return '';
    }
    const parsed = moment(date);
    if (!parsed.isValid()) {
        return '';
    }
    return parsed.format('MMM D, YYYY');
};

const mapResponseToRow = (item) => ({
    id: `${item.coachMemberId}-${item.clubId}`,
    coachMemberId: item.coachMemberId,
    clubId: item.clubId,
    clubName: item.schoolDisplayName || item.clubName,
    coachFirstName: item.coachFirstName,
    coachLastName: item.coachLastName,
    dateCreated: item.dateCreated,
});

const InterestedInMeTab = ({ gender, onAddSuccess, onAddError, onTotalChange }) => {
    const [rows, setRows] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [fetchError, setFetchError] = useState(null);

    const fetchRows = useCallback(async () => {
        setIsLoading(true);
        setFetchError(null);
        try {
            const params = {
                page: 1,
                pageSize: INTERESTED_IN_ME_INLINE_PAGE_SIZE,
                sort: 'date',
                order: 'desc',
                ...(gender ? { gender } : {}),
            };
            const response = await getApiAxiosInstance().get(
                INTERESTED_IN_ME_ENDPOINT,
                { params },
            );
            const results = response?.data?.results || [];
            setRows(results.map(mapResponseToRow));
            if (onTotalChange) {
                onTotalChange(response?.data?.totalCount || 0);
            }
        } catch (err) {
            setFetchError(err?.response?.data?.DisplayMessage || 'We couldn\'t load coach interest.');
            setRows([]);
            if (onTotalChange) {
                onTotalChange(0);
            }
        } finally {
            setIsLoading(false);
        }
    }, [gender, onTotalChange]);

    useEffect(() => {
        fetchRows();
    }, [fetchRows]);

    return (
        <div data-testid={`${TESTID_PREFIX}.container`}>
            <div className={styles.tableContainer} data-testid={`${TESTID_PREFIX}.table`}>
                <div className={styles.tableHeader}>
                    <span className={styles.tableHeaderCell}>College Name</span>
                    <span className={styles.tableHeaderCell}>Coach Name</span>
                    <span className={styles.tableHeaderCell}>Date</span>
                    <span className={styles.tableHeaderCell}>Add to List</span>
                </div>
                {isLoading && rows.length === 0 && (
                    <div
                        className={styles.emptyState}
                        data-testid={`${TESTID_PREFIX}.loading`}
                    >
                        Loading…
                    </div>
                )}
                {!isLoading && fetchError && (
                    <div
                        className={styles.errorState}
                        data-testid={`${TESTID_PREFIX}.error`}
                    >
                        {fetchError}
                    </div>
                )}
                {!isLoading && !fetchError && rows.length === 0 && (
                    <div
                        className={styles.emptyState}
                        data-testid={`${TESTID_PREFIX}.empty`}
                    >
                        No coaches have shown interest yet.
                    </div>
                )}
                {rows.map((row) => {
                    const rowTestId = `${TESTID_PREFIX}.row-${row.id}`;
                    return (
                        <div
                            key={row.id}
                            className={styles.tableRow}
                            data-testid={rowTestId}
                        >
                            <div className={styles.collegeCell}>
                                <a
                                    href={`/clubs/${row.clubId}`}
                                    className={styles.collegeLink}
                                    data-testid={`${rowTestId}.college-link`}
                                >
                                    {row.clubName}
                                </a>
                            </div>
                            <span
                                className={styles.coachCell}
                                data-testid={`${rowTestId}.coach-name`}
                            >
                                {`${row.coachFirstName || ''} ${row.coachLastName || ''}`.trim()}
                            </span>
                            <span
                                className={styles.dateCell}
                                data-testid={`${rowTestId}.date`}
                            >
                                {formatDate(row.dateCreated)}
                            </span>
                            <div className={styles.addCell}>
                                <AddToTargetListButton
                                    clubId={row.clubId}
                                    onSuccess={onAddSuccess}
                                    onError={onAddError}
                                    data-testid={`${rowTestId}.add-btn`}
                                />
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

InterestedInMeTab.defaultProps = {
    gender: null,
    onAddSuccess: null,
    onAddError: null,
    onTotalChange: null,
};

InterestedInMeTab.propTypes = {
    gender: PropTypes.string,
    onAddSuccess: PropTypes.func,
    onAddError: PropTypes.func,
    onTotalChange: PropTypes.func,
};

export default InterestedInMeTab;
